import type { NostrEvent } from '@nostrify/nostrify';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

import { Layout } from '@/components/Layout';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { getArticleMeta, useArticle } from '@/hooks/useArticles';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

interface ArticleViewProps {
  pubkey: string;
  identifier: string;
}

export function ArticleView({ pubkey, identifier }: ArticleViewProps) {
  const { data: event, isLoading } = useArticle(pubkey, identifier);

  if (isLoading) {
    return (
      <Layout>
        <ArticleSkeleton />
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto text-center py-20">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                Article not found. It may have been deleted or the relay may be unreachable.
              </p>
              <Link to="/" className="inline-block mt-4">
                <Button variant="outline">
                  <ArrowLeft className="size-4 mr-2" />
                  Back to Feed
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ArticleContent event={event} />
    </Layout>
  );
}

function ArticleContent({ event }: { event: NostrEvent }) {
  const meta = getArticleMeta(event);
  const author = useAuthor(event.pubkey);
  const authorMeta = author.data?.metadata;
  const displayName = authorMeta?.name ?? genUserName(event.pubkey);

  const npub = nip19.npubEncode(event.pubkey);

  const formattedDate = meta.publishedAt
    ? new Date(meta.publishedAt * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  useSeoMeta({
    title: `${meta.title} | Substr`,
    description: meta.summary ?? `An article by ${displayName}`,
  });

  return (
    <article className="max-w-3xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
        <ArrowLeft className="size-4" />
        Back to Feed
      </Link>

      {meta.image && (
        <img
          src={meta.image}
          alt={meta.title}
          className="w-full max-h-96 object-cover rounded-xl mb-8"
        />
      )}

      <h1 className="text-3xl md:text-5xl font-bold font-serif leading-tight mb-4">
        {meta.title}
      </h1>

      {meta.summary && (
        <p className="text-lg md:text-xl text-muted-foreground mb-6 leading-relaxed">
          {meta.summary}
        </p>
      )}

      <div className="flex items-center gap-4 mb-6">
        <Link to={`/${npub}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <Avatar size="lg">
            {authorMeta?.picture && (
              <AvatarImage src={authorMeta.picture} alt={displayName} />
            )}
            <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{displayName}</p>
            {formattedDate && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="size-3" />
                {formattedDate}
              </p>
            )}
          </div>
        </Link>
      </div>

      {meta.hashtags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <Tag className="size-3.5 text-muted-foreground" />
          {meta.hashtags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <Separator className="mb-8" />

      <MarkdownRenderer content={event.content} className="mb-16" />
    </article>
  );
}

function ArticleSkeleton() {
  return (
    <div className="max-w-3xl mx-auto">
      <Skeleton className="h-5 w-24 mb-6" />
      <Skeleton className="w-full h-72 rounded-xl mb-8" />
      <Skeleton className="h-10 w-3/4 mb-4" />
      <Skeleton className="h-6 w-full mb-2" />
      <Skeleton className="h-6 w-2/3 mb-6" />
      <div className="flex items-center gap-3 mb-8">
        <Skeleton className="size-10 rounded-full" />
        <div className="space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
      <Skeleton className="h-px w-full mb-8" />
      <div className="space-y-4">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-3/4" />
      </div>
    </div>
  );
}
